import {useDispatch, useSelector} from "react-redux";
import React, {useEffect, useState, useMemo, useRef} from "react";
import {Responsive} from "react-grid-layout";
import {appSetAlert} from "../../../../redux/app/appActions";
import {
	findXGridComponent,
	findComponent,
	pageLayoutConfigurationAction,
	pageComponentResizeAction
} from "../../../../redux/page/pageActions";
import useWindowSize from "../../../../hooks/useWindowSize";
import {uuid} from "../../../../utilities/GeneralUtilities";

import _ from "lodash";

import { LinearProgress, styled, IconButton } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

import useDimensions from "react-cool-dimensions";


const PREFIX = 'ParseGridLayout';

const classes = {
	root: `${PREFIX}-root`,
    linearProgress: `${PREFIX}-linearProgress`,
    button: `${PREFIX}-button`,
    buttonPaper: `${PREFIX}-buttonPaper`,
    buttonIcon: `${PREFIX}-buttonIcon`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
	[`&.${classes.root}`]: {
		width: '100%',
		height: '100%',
		margin: '0 auto',
		backgroundColor: theme.palette.background.default
	},

    [`& .${classes.linearProgress}`]: {
		height: '1px'
	},

    [`& .${classes.button}`]: {
		"&:hover": {
			backgroundColor: "transparent"
		},
		zIndex: 9,
		position: 'absolute',
		margin: 0,
		padding: 0
	},

    [`& .${classes.buttonPaper}`]: {
		// backgroundColor: '#2a2a2b',
		backgroundColor: 'rgba(0,0,0,0)',
		margin: '0',
		padding: '0',
		width: '15px',
		height: '15px'
	},

    [`& .${classes.buttonIcon}`]: {
		color: '#606063',
		position: 'absolute',
		bottom: '0',
		right: '0',
		width: '15px',
		height: '15px'
	}
}));

export function ParseGridLayout({container, level, parseComponent, hideListeners, xGridDraggable}) {

	const dispatch = useDispatch();

	const ResponsiveGridLayout = Responsive;

	// console.log('ParseGridLayout ID: ', container.id);
	// console.log('ParseGridLayout CONTAINER: ', container);
	// console.log('ParseGridLayout LEVEL: ', level);

	// App State
	const rightDrawerOpen = useSelector(state => state.app.rightDrawerOpen);
	const pageID = useSelector(state => state.app.pageID);
	const pageConfigs = useSelector(state => state.config.data.pageConfigs);
	// const quickState = useSelector(state => state.page.quickState);
	const pages = useSelector(state => state.page.pages);
	const isEditing = useSelector(state => state.persist.isEditing);
	const activeRoleName = useSelector(state => state.persist.activeRoleName);

	// Component State
	// TODO: Cleanup these guids
	// const [guid, setGUID] = useState(null);
	const [gridGuidComponents, setGridGuidComponents] = useState([]);
	const [layoutWidth, setLayoutWidth] = useState(1);
	const [breakpointLayouts, setBreakpointLayouts] = useState(container.configuration?.layouts !== undefined ? container.configuration.layouts : {})
	const [rowHeight, setRowHeight] = useState(container.configuration?.rowHeight !== undefined ? container.configuration.rowHeight : 160);
	const [margin, setMargin] = useState(container.configuration?.margin !== undefined ? container.configuration.margin : [8,8]);
	const [layout, setLayout] = useState([]);
	const [prevAllLayouts, setPrevAllLayouts] = useState(container.configuration?.layouts !== undefined ? container.configuration.layouts : {});
	const [prevNewItem, setPrevNewItem] = useState({});

	// Rather than relying on useRef, useCallback (within useDimensions hook) ensures that the component containing the ref is notified of changes
	// If not need dimensions, show/hide scenarios, use useClientRec.js in hooks folder
	// https://reactjs.org/docs/hooks-faq.html#how-can-i-measure-a-dom-node
	// https://github.com/wellyshen/react-cool-dimensions?ref=hackernoon.com#how-to-share-a-ref
	const {observe} = useDimensions();
	const size = useWindowSize();
	const ref = useRef();

	const itemLevel = level + 1;

	useEffect(() => {
		const gridWidth = ref.current !== undefined ? ref.current.clientWidth : size.width;
		setLayoutWidth(gridWidth);
		// setGUID(uuid());

		generateGuidGridItems(container);
	}, []);

	// When Container changes or isEditing is toggled, need to rebuild the layout with updated Configuration
	useEffect(() => {
		const gridWidth = ref.current !== undefined ? ref.current.clientWidth : size.width;
		setLayoutWidth(gridWidth);

		generateGuidGridItems(container);
	}, [container, isEditing]);

	useEffect(() => {
		if (window.guid) {
			clearInterval(window.guid);
		}

		if (level < 2) {
			setTimeout(() => {
				handleResize();
			}, 300);
		}
	}, [size, ref.current, rightDrawerOpen, /*quickState*/])

	const handleResize = () => {
		if (ref.current) {
			const canvasWidth = ref.current.clientWidth; // - (rightDrawerOpen ? rightDrawerWidth : 0);
			setLayoutWidth(canvasWidth);
		}
	}

	const handleLayoutChange = (layout, allLayouts) => {
		if (isEditing) {
			// console.log('handleLayoutChange() -> {0} {1}'.format(pageID, container.id));
			// console.log(breakpoint);
			// console.log(pageID);
			// console.log(layout);
			// console.log(allLayouts);

			if (pageID) {
				if (!(_.isEqual(allLayouts, prevAllLayouts))) {
					// console.log('handleLayoutChange() --> did not match');
					dispatch(pageLayoutConfigurationAction(pageID, container.id, allLayouts));
					setBreakpointLayouts(allLayouts);
				}
				// else {
				// 	console.log('parseGridLayout --> matched');
				// }
			}
			setPrevAllLayouts(allLayouts);
		}
	}

	const handleOnBreakpointChange = (breakpoint) => {

		if (isEditing) {
			// console.log('handleOnBreakpointChange: ', breakpoint);
			dispatch(appSetAlert('Breakpoint', breakpoint, 'success', 10000));
		}
	}

	const handleItemResize = (layout, oldItems, newItem, placeholder, e, element) => {
		// console.log('handleItemResize() -> {0} {1}'.format(pageID, newItem.i));
		// console.log(layout);
		// console.log(oldItems);
		// console.log(newItem);
		// console.log(prevNewItem);
		// console.log(placeholder);
		// console.log(e);
		// console.log(element);

		// If Item Resized (even just the handle clicked) is different from the previous item, then mark as componentUpdated in Redux
		if (!(_.isEqual(newItem, prevNewItem))) {
			// console.log('handleItemResize() --> did not match');
			// 1) Find Page in State
			const page = pages.find(page => page.name === pageID);

			// 2) Each page has 3 potential areas, need to loop through each one to find our xGrid
			// break when found
			for (let i = 0; i < pageConfigs.length; i++ ) {
				const roleConfig = page.roles.find(r => r.name === activeRoleName)[pageConfigs[i]];
				if (roleConfig) {

					// Find the current xGrid
					const xGridComponent = findXGridComponent(container.id, roleConfig);

					if (xGridComponent) {
						// Using the current xGrid to find the current component in State (this is to retain any xConfig changes)
						const findResult = findComponent(newItem.i, null, xGridComponent.configuration.components);
						dispatch(pageComponentResizeAction(pageID, newItem.i, findResult.configuration));
						// console.log('**SUCCESS** xGridComponent Found {0}'.format(container.id));
						break;
					}
					// else {
					// 	console.log('**ERROR** Could not find xGridComponent {0}'.format(container.id));
					// }
				}
			}
		}
		// else {
		// 	console.log('handleItemResize() --> matched');
		// }
		setPrevNewItem(newItem);
	}

	const generateGuidGridItems = (itemsContainer) => {
		// Build Grid with proper Layouts object and IDs, else will generate LG Layout and IDs
		// const itemLevel = level + 1;
		// console.log('Level: ', level);
		// console.log('itemLevel: ', itemLevel);


		// 1/3: Generate Guid for Container ID
		if (itemsContainer.id === undefined) {
			itemsContainer.id = uuid();
		}
		// console.log('%c Container ID w/ Guid -------> ', 'font-size: 14px; font-weight: bold; color: blue', itemsContainer.id);
		// console.log('%c Container: ', 'font-size: 14px; font-weight: bold; color: blue', itemsContainer);


		// 2/3: Generate Guid for Layouts
		let layouts = itemsContainer.configuration.layouts;
		const components = itemsContainer.configuration.components;
		components.index = -1;
		let guidComponents = components;

		if (layouts === undefined) {
			// console.log('Layouts undefined ---> Building layout now ...');
			// console.log(components);

			guidComponents = components.map(c => {
				if (c.id === undefined || c.id === "123" || c.id === 123) {
					c.id = uuid();
					return c;
				} else if (c.id !== undefined) {
					return c;
				}
			});
			// console.log(guidComponents);

			let largeLayout = [];
			for (let c = 0; c < guidComponents.length; c++) {
				const lgLayout = {
					"i": guidComponents[c].id,
					"x": 0,
					"y": c,
					"w": 12,
					"h": 1
				}
				largeLayout.push(lgLayout);
			}

			layouts = {};
			layouts.lg = largeLayout;
			// console.log('%c LG Layouts w/ Guid -------> ', 'font-size: 14px; font-weight: bold; color: green', layouts);
			// console.log('%c Components Layouts w/ Guid -------> ', 'font-size: 14px; font-weight: bold; color: purple', guidComponents);
		}


		// 3/3: Set the Layout and GridGuidComponents
		for (const l in layouts) {
			if (layouts[l].length > 0) {
				// console.log(layouts[l]);
				setLayout(layouts[l]);
				setGridGuidComponents(guidComponents);
				break;
			}
		}

		// Only comment out when debugging non-draggable pages!!!!
		if (layout == null) {
			return null;
		}
	}

	const buildChildren = () => {
		if (layout.length > 0) {
			return layout.map((c) => {
				// console.log(layout);
				// console.log(gridGuidComponents);

				const component = gridGuidComponents.find(t => t.id === c.i);
				const parentStyle = component?.wrapper?.parent;
				const newLevelNoDrag = itemLevel === 0 ? '' : (itemLevel === 1 ? 'no-drag' : 'no-drag' + (itemLevel - 1));
				const dragBorderStyle = isEditing ? {border: "1px dashed gray"} : {borderStyle: "none"};

				return <div key={c.i} className={newLevelNoDrag} style={{...parentStyle, ...dragBorderStyle}}>
                                {parseComponent(component,
                                                hideListeners,
                                                component.type.toLowerCase() === 'xgrid' ? itemLevel
                                                : component.type.toLowerCase() === 'tabfilter' ? itemLevel
                                                : component.type.toLowerCase() === 'tab' ? itemLevel
                                                : component.type.toLowerCase() === 'grid' ? itemLevel
                                                : level,
                                                true,
                                                null,
                                                null,
                                                null)}
                            </div>
			});
		}
	}
	const children = useMemo(() => buildChildren(), [layout, gridGuidComponents, isEditing]);

	// console.log(layoutWidth);
	// console.log(container.configuration);
	// console.log(gridGuidComponents);
	// console.log(margin);

	const parentStyle = {
		...container.wrapper.parent
	}

	return (
		<Root className={classes.root} style={parentStyle}
			 ref={(el) => {
				observe(el); // Set the target element for measuring
				ref.current = el; // Share the element for other purposes
			}}>
			{!layoutWidth &&
				<LinearProgress className={classes.linearProgress}/>
			}
			{layoutWidth && breakpointLayouts && gridGuidComponents.length > 0 &&
				<React.Fragment>
					<ResponsiveGridLayout className={"layout"}
										  layouts={_.cloneDeep(breakpointLayouts)}
										  onLayoutChange={handleLayoutChange}
										  onBreakpointChange={handleOnBreakpointChange}
										  onResizeStop={handleItemResize}
										  breakpoints={{lg: 1536, md: 1200, sm: 900, xs: 600, xxs: 0}}
										  cols={{lg: 12, md: 12, sm: 12, xs: 12, xxs: 12}}
										  margin={margin}
										  // containerPadding={[level >= 1 ? 0 : 20, level >= 1 ? 0 : 8]}
										  width={layoutWidth}
										  rowHeight={rowHeight}
										  autoSize={true}
										  allowOverlap={false}
										  isDraggable={isEditing}
										  isResizable={isEditing}
										  isBounded={true}
										  draggableHandle={".drag-handle"}
										  draggableCancel={level === -1 ? ".no-drag" :  (level === 0 ? ".no-drag"+(itemLevel) : ".no-drag"+(itemLevel))}
										  compactType={'vertical'}
					>
						{children}
					</ResponsiveGridLayout>
					{(xGridDraggable) && (isEditing && (itemLevel >= 0)) &&
						<IconButton className={classes.button} style={{bottom: '4px', right: '2px'}} color="primary"
									aria-label="drag" component="span">
							<div className={["drag-handle", classes.buttonPaper].join(' ')}>
								<DragIndicatorIcon className={classes.buttonIcon}/>
							</div>
						</IconButton>
					}
				</React.Fragment>
			}
		</Root>
	)
}